import { PROJECT_REFRESH_INTERVAL_MS, hasItems, isDefined } from "@repo/utils";
import useSWR, { mutate } from "swr";

import * as fetcher from "../fetcher";
import { type Order } from "./types";

export function useOrder(id?: string) {
  return useSWR<Order>(
    id ? `/orders/${id}` : null,
    (url) => fetcher.global.get(url),
    {
      shouldRetryOnError: false,
      revalidateOnFocus: false,
      revalidateIfStale: false,
      dedupingInterval: PROJECT_REFRESH_INTERVAL_MS,
      keepPreviousData: true,
    }
  );
}

export function useOrders(ids?: string[]) {
  return useSWR<Order[]>(
    // Adding many here to avoid getting the same key as single order
    hasItems(ids) ? `many /global/orders/${ids.join(",")}` : [],
    () => {
      const promises =
        ids?.map((id) => fetcher.global.get<Order>(`/orders/${id}`)) ?? [];

      return Promise.allSettled(promises).then((results) =>
        results
          .flatMap((r) => {
            if (r.status === "fulfilled") {
              return r?.value;
            }
            return undefined;
          })
          .filter(isDefined)
      );
    },
    {
      shouldRetryOnError: false,
      revalidateOnFocus: false,
      dedupingInterval: PROJECT_REFRESH_INTERVAL_MS,
      keepPreviousData: true,
    }
  );
}

export const deleteOrderInfo = async (
  opportunityId?: number,
  orderId?: string
) => {
  const res = await fetcher.opportunity.remove(
    `/${opportunityId}/order/${orderId}`
  );
  mutate(`/opportunities/${opportunityId}`);
  mutate(`/opportunities/${opportunityId}/history`);
  return res;
};

export const associateOrders = async (
  orderIds: string[],
  opportunityId: number
) => {
  const res = await fetcher.opportunity.post<null, true>(
    `/${opportunityId}/multiorders`,
    orderIds.map((orderId) => ({ orderId: orderId, isDraftOrder: false })),
    undefined,
    true
  );
  mutate(`/opportunities/${opportunityId}`);
  mutate(`/opportunities/${opportunityId}/history`);
  return res;
};
